import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {ClickEventHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {updatedSavedViewIsPinnedAction} from '../../../store/panels/analytics/views/analyticsPanelViewsActions';
import {AnalyticsSavedView} from '../../../store/panels/analytics/views/analyticsPanelViewsState';

/*
 * Props.
 */

export interface TabPinIconProps {
  isPinned: boolean;
  view: AnalyticsSavedView;
}

/*
 * Styles.
 */

const StyledIcon = styled(Icon)`
  margin-right: 4px;
  padding: 0;

  // Avoid missed mouseenter/mouseleave events in Chrome due to this DOM subtree being added/removed from the document.
  pointer-events: none;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  min-width: auto;
`;

/*
 * Component.
 */

export const TabPinIcon: FC<TabPinIconProps> = ({isPinned, view}) => {
  const theme = useUserTheme();
  const dispatch = useAppDispatch();

  const [isHovered, setIsHovered] = useState(false);
  // If the icon was clicked during the current hover cycle, then we don't want to use
  // the hover icon state, but rather reflect the current true state (basically to avoid
  // just alternating hover states right after clicks).
  const [wasClickedDuringCurrentHover, setWasClickedDuringCurrentHover] = useState(false);

  const iconName = isPinned ? 'pinFilledSmall' : 'pinOutlineSmall';
  const hoverIconName = isPinned ? 'unpinOutlineSmall' : 'pinOutlineSmall';

  const onClick: ClickEventHandler = (event) => {
    event.stopPropagation();

    setWasClickedDuringCurrentHover(true);
    dispatch(updatedSavedViewIsPinnedAction(view, !view.isPinned));
  };

  return (
    <StyledTooltipCoordinator render={() => renderTooltipContent(isPinned, wasClickedDuringCurrentHover)}>
      <ChromelessButton
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => {
          setIsHovered(false);
          setWasClickedDuringCurrentHover(false);
        }}
        onClick={onClick}
      >
        <StyledIcon
          name={isHovered && !wasClickedDuringCurrentHover ? hoverIconName : iconName}
          color={isPinned || isHovered ? theme.palette.blue.shade40 : theme.greys.shade50}
        />
      </ChromelessButton>
    </StyledTooltipCoordinator>
  );
};

/*
 * Helpers.
 */

function renderTooltipContent(isPinned: boolean, wasClickedDuringCurrentHover: boolean) {
  // This is just: isPinned XOR clickedDuringCurrentHover
  return (isPinned && !wasClickedDuringCurrentHover) || (!isPinned && wasClickedDuringCurrentHover) ? (
    <Trans id="analytics-tabs-unpin-view-icon">Unpin view</Trans>
  ) : (
    <Trans id="analytics-tabs-pin-view-icon">Pin view</Trans>
  );
}
